/**
 *
 * CustomResumeOverlay - Blue Shield of California
 *
 */

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import useSiteCopySelector from 'components/useSiteCopySelector';
import _get from 'lodash/get';
import { getParentPosition } from 'utils/embedded';

const useStyles = makeStyles(theme => ({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: '24px',
    zIndex: 1300,
  },
  modal: {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    boxShadow:
      '0px 20px 40px rgba(34, 10, 51, 0.2), 0px 0px 15px rgba(34, 10, 51, 0.05)',
    padding: '48px 88px',
    maxWidth: '670px',
    width: '90%',
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      padding: '32px 24px',
      maxWidth: '340px',
    },
  },
  content: {
    textAlign: 'center',
  },
  title: {
    color: theme.palette.text.primary,
    marginBottom: '48px',
    fontWeight: 600,
    fontSize: '32px',
    lineHeight: '38px',
    letterSpacing: '0px',
    textAlign: 'center',
    fontFamily: theme.typography.fontFamily,
    [theme.breakpoints.down('sm')]: {
      fontSize: '20px',
      lineHeight: '28px',
      marginBottom: '24px',
    },
  },
  buttonContainer: {
    display: 'flex',
    gap: '24px',
    justifyContent: 'center',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      gap: '12px',
    },
  },
  startOverButton: {
    width: 170,
    height: 56,
    borderRadius: 32,
    gap: 8,
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
    textTransform: 'none',
    fontWeight: 600,
    lineHeight: '24px',
    fontSize: '18px',
    fontFamily: theme.typography.fontFamily,
    border: 'none !important',
    '&:hover': {
      backgroundColor: 'transparent',
      color: theme.palette.bgColor.default,
    },
    [theme.breakpoints.down('sm')]: {
      width: 'auto',
      gap: 8,
    },
  },
  proceedButton: {
    width: 170,
    height: 56,
    borderRadius: 32,
    backgroundColor: theme.palette.primary.main,
    color: '#FFFFFF',
    textTransform: 'none',
    boxShadow: 'none',
    fontWeight: 600,
    lineHeight: '24px',
    fontSize: '18px',
    fontFamily: theme.typography.fontFamily,
    '&:hover': {
      backgroundColor: theme.palette.bgColor.default,
    },
    [theme.breakpoints.down('sm')]: {
      width: 'auto',
    },
  },
}));

const CustomResumeOverlay = ({ onClick, visible }) => {
  const classes = useStyles();
  const [siteCopy] = useSiteCopySelector(['assessment']);
  const [embedParentPosition, setEmbedParentPosition] = useState({});

  const handleResume = () => onClick(true);
  const handleStartOver = () => onClick(false);

  useEffect(() => {
    let isMounted = true;

    const fetchAndSetParentPosition = async () => {
      const parentPosition = await getParentPosition();
      if (isMounted) {
        setEmbedParentPosition(parentPosition);
      }
    };
    if (visible) {
      fetchAndSetParentPosition();
    }

    return () => {
      isMounted = false;
    };
  }, [visible]);

  if (!visible) return null;

  const embedPositionTop = _get(embedParentPosition, 'top', 0);

  // Apply the parent scroll position directly to the overlay style
  const overlayStyle = {
    top: `${embedPositionTop}px`,
    position: 'absolute',
    width: '100%',
  };

  return (
    <Box className={classes.overlay} style={overlayStyle}>
      <Box className={classes.modal}>
        <Box className={classes.content}>
          <Typography className={classes.title}>
            {_get(siteCopy, 'pageCopy.resumeModalHeader', '')}
          </Typography>
          <Box className={classes.buttonContainer}>
            <Button
              variant="text"
              onClick={handleStartOver}
              className={classes.startOverButton}
            >
              {_get(siteCopy, 'pageCopy.resumeModalRefuseLabel', '')}
            </Button>
            <Button
              variant="contained"
              onClick={handleResume}
              className={classes.proceedButton}
            >
              {_get(siteCopy, 'pageCopy.resumeModalConfirmLabel', '')}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

CustomResumeOverlay.propTypes = {
  onClick: PropTypes.func.isRequired,
  visible: PropTypes.bool,
};

CustomResumeOverlay.defaultProps = {
  visible: false,
};

export default CustomResumeOverlay;
